import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TextInput,
  StyleSheet,
  Switch,
  ScrollView,
  Platform,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Club } from '../types';
import { Button } from './Button';
import { Colors, Fonts, Radii, Shadows, Typography } from '../constants';

interface ClubEditModalProps {
  visible: boolean;
  club: Club;
  isAdmin: boolean; // Controls is_verified toggle visibility
  onClose: () => void;
  onSave: (updates: Partial<Club>) => Promise<void>;
  loading?: boolean;
}

export const ClubEditModal: React.FC<ClubEditModalProps> = ({
  visible,
  club,
  isAdmin,
  onClose,
  onSave,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState(club.name);
  const [description, setDescription] = useState(club.description || '');
  const [location, setLocation] = useState(club.location || '');
  const [isVerified, setIsVerified] = useState(club.is_verified);
  const [transactionFee, setTransactionFee] = useState(
    club.transaction_fee_amount?.toString() || '1.00'
  );
  const [feeError, setFeeError] = useState<string | null>(null);
  const [saveLoading, setSaveLoading] = useState(false);

  // Reset form when club changes
  useEffect(() => {
    setName(club.name);
    setDescription(club.description || '');
    setLocation(club.location || '');
    setIsVerified(club.is_verified);
    setTransactionFee(club.transaction_fee_amount?.toString() || '1.00');
    setFeeError(null);
  }, [club]);

  const validateFee = (value: string): boolean => {
    const fee = parseFloat(value);
    const min = club.min_transaction_fee || 1;
    const max = club.max_transaction_fee || 10;

    if (isNaN(fee) || fee < min || fee > max) {
      setFeeError(
        t('screens.manageClubs.errorFeeOutOfRange', {
          min: min.toFixed(2),
          max: max.toFixed(2),
        })
      );
      return false;
    }
    setFeeError(null);
    return true;
  };

  const handleFeeChange = (value: string) => {
    // Allow only valid decimal input (single decimal point)
    let cleaned = value.replace(/[^0-9.]/g, '');
    // Prevent multiple decimal points
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    setTransactionFee(cleaned);
    if (cleaned) {
      validateFee(cleaned);
    } else {
      setFeeError(null);
    }
  };

  const handleSave = async () => {
    // Validate name
    const trimmedName = name.trim();
    if (!trimmedName) {
      // Parent component handles error dialogs via onSave rejection
      throw new Error(t('screens.manageClubs.errorNameRequired'));
    }
    if (trimmedName.length < 2) {
      throw new Error(t('screens.manageClubs.errorNameTooShort'));
    }

    setSaveLoading(true);
    try {
      const updates: Partial<Club> = {
        name: trimmedName,
        description: description.trim() || null,
        location: location.trim() || null,
      };

      // Validate and include fee if admin
      if (isAdmin) {
        updates.is_verified = isVerified;

        if (!validateFee(transactionFee)) {
          setSaveLoading(false);
          return;
        }
        updates.transaction_fee_amount = parseFloat(transactionFee);
      }

      await onSave(updates);
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      <View style={styles.modalOverlay}>
        <View style={styles.modalContent}>
          <ScrollView showsVerticalScrollIndicator={false}>
            <Text style={styles.modalTitle}>
              {t('screens.manageClubs.editClub')}
            </Text>

            {/* Club Name */}
            <Text style={styles.label}>
              {t('screens.manageClubs.clubName')} *
            </Text>
            <TextInput
              style={styles.input}
              value={name}
              onChangeText={setName}
              placeholder={t('screens.manageClubs.clubName')}
              placeholderTextColor={Colors.textLight}
              autoCapitalize="words"
            />

            {/* Description */}
            <Text style={styles.label}>
              {t('screens.manageClubs.clubDescription')}
            </Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={description}
              onChangeText={setDescription}
              placeholder={t('screens.manageClubs.clubDescription')}
              placeholderTextColor={Colors.textLight}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />

            {/* Location */}
            <Text style={styles.label}>
              {t('screens.manageClubs.location')}
            </Text>
            <TextInput
              style={styles.input}
              value={location}
              onChangeText={setLocation}
              placeholder={t('screens.manageClubs.locationPlaceholder')}
              placeholderTextColor={Colors.textLight}
              autoCapitalize="words"
            />

            {/* Admin-only: Verified toggle and Fee */}
            {isAdmin && (
              <View style={styles.adminSection}>
                <Text style={styles.adminSectionTitle}>
                  {t('screens.manageClubs.adminOnly')}
                </Text>

                {/* Transaction Fee */}
                <View style={styles.feeInputContainer}>
                  <Text style={styles.switchLabel}>
                    {t('screens.manageClubs.feeLabel')}
                  </Text>
                  <View style={styles.feeInputRow}>
                    <Text style={styles.currencyPrefix}>€</Text>
                    <TextInput
                      style={[styles.feeInput, feeError && styles.inputError]}
                      value={transactionFee}
                      onChangeText={handleFeeChange}
                      keyboardType="decimal-pad"
                      placeholder={t('screens.manageClubs.feePlaceholder')}
                      placeholderTextColor={Colors.textLight}
                      maxLength={5}
                    />
                  </View>
                  <Text style={styles.feeHelp}>
                    {t('screens.manageClubs.feeHelp')}
                  </Text>
                  <Text style={styles.feeRangeText}>
                    {t('screens.manageClubs.feeRange', {
                      min: (club.min_transaction_fee || 1).toFixed(2),
                      max: (club.max_transaction_fee || 10).toFixed(2),
                    })}
                  </Text>
                  {feeError && <Text style={styles.errorText}>{feeError}</Text>}
                </View>

                <View style={styles.switchRow}>
                  <Text style={styles.switchLabel}>
                    {t('screens.manageClubs.isVerified')}
                  </Text>
                  <Switch
                    value={isVerified}
                    onValueChange={setIsVerified}
                    trackColor={{ false: Colors.border, true: Colors.primary }}
                    thumbColor={Colors.white}
                  />
                </View>
              </View>
            )}

            {/* Actions */}
            <View style={styles.modalActions}>
              <Button
                title={t('common.buttons.cancel')}
                onPress={onClose}
                variant="secondary"
                disabled={saveLoading || loading}
                style={styles.actionButton}
              />
              <Button
                title={t('common.buttons.save')}
                onPress={handleSave}
                loading={saveLoading || loading}
                variant="primary"
                style={styles.actionButton}
              />
            </View>
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    backgroundColor: Colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: Colors.white,
    borderRadius: Radii.dialog,
    padding: 28,
    width: '90%',
    maxWidth: 500,
    maxHeight: '80%',
    ...Shadows.panel,
  },
  modalTitle: {
    ...Typography.section,
    marginBottom: 20,
    color: Colors.textDark,
  },
  label: {
    ...Typography.label,
    color: Colors.textDark,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.control,
    padding: 12,
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
    marginBottom: 16,
    backgroundColor: Colors.white,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  textArea: {
    height: 100,
    textAlignVertical: 'top',
  },
  adminSection: {
    backgroundColor: Colors.background,
    borderRadius: Radii.card,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.borderLight,
  },
  adminSectionTitle: {
    ...Typography.overline,
    color: Colors.textLight,
    marginBottom: 12,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  switchLabel: {
    fontFamily: Fonts.semiBold,
    fontSize: 14,
    color: Colors.textDark,
  },
  feeInputContainer: {
    marginBottom: 16,
  },
  feeInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  currencyPrefix: {
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    color: Colors.textMedium,
    marginRight: 6,
  },
  feeInput: {
    height: 44,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.control,
    paddingHorizontal: 12,
    fontFamily: Fonts.semiBold,
    fontSize: 15,
    color: Colors.text,
    width: 120,
    backgroundColor: Colors.white,
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none' as any,
    }),
  },
  inputError: {
    borderColor: Colors.error,
  },
  feeHelp: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textLight,
    marginTop: 6,
  },
  feeRangeText: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.textLight,
    marginTop: 2,
  },
  errorText: {
    fontFamily: Fonts.regular,
    fontSize: 12,
    color: Colors.error,
    marginTop: 6,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  actionButton: {
    flex: 1,
  },
});
